// Accordion breakpoint: mobile <= 749px, desktop >= 750px.
const accordionMediaQueryLarge = window.matchMedia('(min-width: 750px)');
const accordionReducedMotion = window.matchMedia(
  '(prefers-reduced-motion: reduce)'
);
function isAccordionMobileBreakpoint() {
  return !accordionMediaQueryLarge.matches;
}
class AccordionCustom extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {Animation | null} */
  #animation = null;
  #targetOpen = false;
  get details() {
    const details = this.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) {
      throw new Error('Details element not found');
    }
    return details;
  }
  get summary() {
    const summary = this.details.querySelector('summary');
    if (!(summary instanceof HTMLElement)) {
      throw new Error('Summary element not found');
    }
    return summary;
  }
  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }
  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }
  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }
  connectedCallback() {
    // Recreate listeners when the two-column script moves this element.
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#setDefaultOpenState();
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, {
      signal,
    });
    this.details.addEventListener('click', this.#handleContentClick, {
      signal,
    });
    accordionMediaQueryLarge.addEventListener(
      'change',
      this.#handleMediaQueryChange,
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.#animation) {
      this.#cancelAnimation();
      this.details.open = this.#targetOpen;
    }
  }
  #cancelAnimation() {
    if (!this.#animation) return;

    this.#animation.onfinish = null;
    this.#animation.cancel();
    this.#animation = null;
  }
  handleClick = (event) => {
    event.preventDefault();
    const isMobile = isAccordionMobileBreakpoint();
    if (
      (isMobile && this.#disableOnMobile) ||
      (!isMobile && this.#disableOnDesktop)
    ) {
      return;
    }
    const currentOpen = this.#animation
      ? this.#targetOpen
      : this.details.open;
    this.#animateTo(!currentOpen);
  };
  #animateTo(open) {
    const details = this.details;
    // Capture the current visual height before cancelling.
    // This also handles another click during an animation.
    const startHeight = details.getBoundingClientRect().height;
    this.#cancelAnimation();
    this.#targetOpen = open;
    const disableAnimation =
      accordionReducedMotion.matches ||
      (!isAccordionMobileBreakpoint() &&
        this.dataset.disableAnimationOnDesktop === 'true');
    if (disableAnimation || typeof details.animate !== 'function') {
      details.open = open;
      return;
    }
    // Measure the natural height of the requested final state.
    // These synchronous changes happen before the next browser paint.
    details.open = open;
    const endHeight = details.getBoundingClientRect().height;
    // Content must remain rendered throughout the closing animation.
    details.open = true;
    if (Math.abs(startHeight - endHeight) < 1) {
      details.open = open;
      return;
    }
    const animation = details.animate(
      [
        {
          height: `${startHeight}px`,
          overflow: 'hidden',
        },
        {
          height: `${endHeight}px`,
          overflow: 'hidden',
        },
      ],
      {
        duration: 450,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'both',
      }
    );
    this.#animation = animation;
    animation.onfinish = () => {
      if (this.#animation !== animation) return;
      details.open = open;
      this.#animation = null;
      // Release the fixed height so content can resize naturally.
      animation.onfinish = null;
      animation.cancel();
    };
  }
  #handleContentClick = (event) => {
    if (!(event.target instanceof Element)) return;
    // Ignore events belonging to a nested accordion.
    if (event.target.closest('accordion-custom') !== this) return;
    if (!event.target.closest('[data-accordion-close]')) return;
    if (!this.details.open) return;
    this.summary.focus({ preventScroll: true });
    this.#animateTo(false);
  };
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };
  #setDefaultOpenState() {
    this.#cancelAnimation();
    const isMobile = isAccordionMobileBreakpoint();
    const open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
    this.#targetOpen = open;
    this.details.open = open;
  }
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#closeWithEscape) return;
    if (
      event.target instanceof Element &&
      event.target.closest('accordion-custom') !== this
    ) {
      return;
    }
    event.preventDefault();
    this.summary.focus({ preventScroll: true });
    this.#animateTo(false);
  };
}
if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}